
import { Amount, Product } from "./product";
import { Shopify } from "./shopify";

// A shopify collection
export class Collection {
    title: string;
    handle: string;
    description: string;
    imageUrl: string | undefined;
    productHandles: Array<string> = [];
    products: Array<Product> = [];

    constructor(title: string, handle: string, description: string) {
        this.title = title;
        this.handle = handle;
        this.description = description;
    }
}

// Get first collection that product belongs to
export async function getFirstCollectionForProduct(productHandle: string) : Promise<Collection | null> {
    const query = `
        query getFirstCollectionForProduct($handle: String!) {
            product(handle: $handle) {
                collections(first: 1) {
                    edges {
                        node {
                            title
                            handle
                            products(first: 20) {
                                edges {
                                    node {
                                        handle
                                        title
                                        featuredImageThumb: featuredImage {
                                            url(transform: { maxHeight: 270, maxWidth: 200, crop: CENTER })
                                        }
                                        priceRange {
                                            minVariantPrice {
                                                amount
                                                currencyCode
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }`;

    const shopify = Shopify.getInstance();
    const {data, errors} = await shopify.request(query, {
        variables: {
            handle: productHandle,
        },
    });

    if(errors) {
        console.error('GraphQL errors:', JSON.stringify(errors, null, 2));
        throw new Error(`Error getting first collection for product: ${productHandle}`);
    }

    const productData = data.product;

    if(productData === undefined || productData === null) {
        throw new Error(`Product object not found for handle: ${productHandle}`);
    }

    const collectionData = productData.collections;

    if(collectionData === undefined || collectionData === null) {
        throw new Error(`Collections object not found for product handle: ${productHandle}`);
    }

    if(collectionData.edges.length === 0) {
        // No collections found, return null to indicate this product is not in any collection
        return null;
    }

    // Get the first collection node
    const firstCollectionData = collectionData.edges[0]?.node;

    if(firstCollectionData === undefined || firstCollectionData === null) {
        return null;
    }

    // Check for handle
    if(firstCollectionData.handle === undefined || firstCollectionData.handle === null) {
        throw new Error(`Collection handle not found for product handle: ${productHandle}`);
    }

    let collection = new Collection(firstCollectionData.title, firstCollectionData.handle, "");

    // Check for products in the collection
    const productsData = firstCollectionData.products;
    if(productsData == undefined && productsData == null) {
        throw new Error(`Products object not found for collection handle: ${firstCollectionData.handle}`);
    }
    const productEdges = productsData.edges;

    for(let i = 0; i < productEdges.length; i++) {
        const productData = productEdges[i].node;

        // Product data should have handle
        if(productData.handle === undefined || productData.handle === null) {
            throw new Error(`Product handle not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        // Product data should have title
        if(productData.title === undefined || productData.title === null) {
            throw new Error(`Product title not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        // Product data should have featuredImageThumb
        if(productData.featuredImageThumb === undefined || productData.featuredImageThumb === null) {
            throw new Error(`Product featuredImageThumb not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        // Product data should have priceRange
        if(productData.priceRange === undefined || productData.priceRange === null) {
            throw new Error(`Product priceRange not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        // Product data should have minVariantPrice within priceRange
        if(productData.priceRange.minVariantPrice === undefined || productData.priceRange.minVariantPrice === null) {
            throw new Error(`Product priceRange.minVariantPrice not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        let minVariantPriceData = productData.priceRange.minVariantPrice;
        if(minVariantPriceData.amount === undefined || minVariantPriceData.amount === null) {
            throw new Error(`Product priceRange.minVariantPrice amount not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        if(minVariantPriceData.currencyCode === undefined || minVariantPriceData.currencyCode === null) {
            throw new Error(`Product priceRange.minVariantPrice currencyCode not found for product in collection handle: ${firstCollectionData.handle}`);
        }
        let amount = new Amount(parseFloat(minVariantPriceData.amount), minVariantPriceData.currencyCode);

        let product = new Product(productData.title, /*description*/ "", amount, productData.handle);
        product.featuredImageThumb = productData.featuredImageThumb.url;

        collection.products.push(product);
    }

    return collection;
}

export async function getCollectionByHandle(handle: string) : Promise<Collection> {
    const shopify = Shopify.getInstance();

    // Use Shopify's GraphQL API to get the collection by handle
    const query = `
        query getCollectionByHandle($handle: String!) {
            collection(handle: $handle) {
                title,
                image {
                    url(transform: { maxHeight: 300, maxWidth: 300, crop: CENTER })
                },
                description,
                products(first: 20) {
                    edges {
                        node {
                            handle
                        }
                    }
                }
            }
        }`;

    // Use query with the shopify.request method
    const {data, errors, extensions} = await shopify.request(query, {
        variables: {
            handle: handle,
        },
    });

    if(errors) {
        throw new Error(`Error getting collection by handle: ${handle}`);
    }

    const collectionData = data.collection;

    if(collectionData === undefined || collectionData === null) {
        throw new Error(`Collection object not found for handle: ${handle}`);
    }

    const collection = new Collection(collectionData.title, handle, collectionData.description);
    if(collectionData.image !== undefined && collectionData.image !== null) {
        collection.imageUrl = collectionData.image.url;
    }
    
    // Get the product handles from the collectionData
    const products = collectionData.products.edges;
    for(let i = 0; i < products.length; i++) {
        const product = products[i].node;
        collection.productHandles.push(product.handle);
    }

    return collection;
}